import { useState } from 'react'
import { format, parseISO } from 'date-fns'
import { Lock } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import { useIsSuperUser } from '@/hooks/useIsSuperUser'
import { useBillingCreditsQuery } from '../hooks/useBillingCreditsQuery'
import { AddCreditDialog } from './AddCreditDialog'
import { CreditsTable } from './CreditsTable'
import { PeriodTitle } from './PeriodTitle'

interface CreditsCardProps {
  /** YYYY-MM */
  period: string
  isCurrent: boolean
  asOfDate: Date
  /** YYYY-MM-DD: the first day relay accepts as a credit's date, from its
   *  current usage; undefined until that has loaded. */
  earliestCreditDate: string | undefined
}

/** The purchase and compensation credits behind the Minutes Ledger's Added
 *  line for the period on screen. */
export function CreditsCard({
  period,
  isCurrent,
  asOfDate,
  earliestCreditDate,
}: CreditsCardProps) {
  const credits = useBillingCreditsQuery(period)
  const isSuperUser = useIsSuperUser()
  const [addCreditOpen, setAddCreditOpen] = useState(false)
  const monthLabel = format(parseISO(`${period}-01`), 'MMMM yyyy')

  return (
    <section
      aria-labelledby="billing-credits-heading"
      className="bg-card rounded-lg shadow-sm border border-border p-6 mb-6">
      <div className="mb-4 flex items-center justify-between gap-4">
        <h2
          id="billing-credits-heading"
          className="text-xs font-semibold tracking-widest text-muted-foreground uppercase">
          <PeriodTitle
            title="Credits Added"
            isCurrent={isCurrent}
            asOfDate={asOfDate}
          />
        </h2>
        {isSuperUser && isCurrent && earliestCreditDate !== undefined && (
          <Button
            variant="outline"
            size="sm"
            className="hidden-on-print"
            title="Careforce global admins only"
            onClick={() => setAddCreditOpen(true)}>
            <Lock aria-hidden="true" />
            Add credit
          </Button>
        )}
      </div>

      {credits.isError ? (
        <p className="text-sm text-error-low-contrast">
          Couldn&rsquo;t load credits. Refresh the page to try again.
        </p>
      ) : !credits.isSuccess ? (
        <div className="flex justify-center py-6">
          <Spinner className="size-6 text-muted-foreground" />
        </div>
      ) : credits.data.credits.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No credits were added in {monthLabel}.
        </p>
      ) : (
        <CreditsTable credits={credits.data.credits} />
      )}
      {earliestCreditDate !== undefined && (
        <AddCreditDialog
          open={addCreditOpen}
          onOpenChange={setAddCreditOpen}
          period={period}
          earliestCreditDate={earliestCreditDate}
        />
      )}
    </section>
  )
}
